import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronDown, ChevronUp, ExternalLink, FileText, House, LogOut, Settings } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import type { HomeAlert } from "../../shared/home";
import type { ToolMeta, ToolSection } from "../../shared/tools";
import { type ComingSoonTool, comingSoonTools, webTools } from "../../tools/web";
import { api, inFrame, phoneView, signedInUrl } from "../lib/api";
import { CompanyContext } from "../lib/company";
import { useHome } from "../lib/home";
import type { CompanySummary, Me } from "../lib/queries";
import type { ConnectionState } from "../lib/realtime";
import { themeLocked } from "../lib/theme";
import { ConnectionBanner } from "./ConnectionBanner";
import { Icon } from "./Icon";
import { SyncLine, SyncStatus } from "./SyncStatus";
import { ThemePicker } from "./ThemePicker";
import { useWithWindows } from "./ToolWindows";
import { SearchButton, SearchHost } from "./SearchAnywhere";
import { Popover } from "./ui";

/*
 * The frame around every page, made to look like part of ServiceM8.
 *  - Wide screens (in a browser, and inside ServiceM8, whose own bar takes the top of the screen):
 *    a sidebar, so the page keeps its height. Home and the tools at the top, each tool's sections
 *    under it; at the bottom, Settings (apart from the tools: it's used now and then), then you,
 *    your business and the live sync status (the account menu: theme, except inside ServiceM8 on
 *    the web; Settings; sign out).
 *  - Narrow screens and phones: tabs across the top (in a browser, under a top bar with the
 *    business, sync status and account menu). Tools show their sections as tabs of their own then.
 */
export function Shell(props: Readonly<{ me: Me; companyId: string; realtime: ConnectionState; children: ReactNode }>) {
    const { me, companyId, realtime } = props;
    const company = me.companies.find((c) => c.id === companyId);
    // Every link between pages keeps the open windows open (ToolWindows.tsx), as the sidebar's do.
    const withWindows = useWithWindows();
    if (!company) return <Redirect to="~/" replace />;

    const tools = company.tools.map((id) => webTools[id]?.meta).filter((m): m is ToolMeta => m !== undefined);
    const showTopBar = !inFrame && !phoneView;
    // Settings: Appearance for everyone, the rest for managers (pages/settings). Not on phones.
    const settings = !phoneView;

    return (
        <CompanyContext value={company}>
            <SearchHost toolIds={company.tools}>
                <div className="flex h-full">
                    {phoneView ? null : (
                        <aside className="hidden w-56 shrink-0 flex-col border-r border-line bg-surface md:flex" aria-label="Sections">
                            <Link href={withWindows("/")} className="block border-b border-line px-4 py-3 no-underline">
                                <span className="block font-bold text-ink">Outback Workbench</span>
                                <span className="block truncate text-xs text-muted">{company.name}</span>
                            </Link>
                            {/* Search across every tool, from any page (Ctrl+K, ⌘K or "/" too). */}
                            <div className="px-2 pt-3">
                                <SearchButton />
                            </div>
                            <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
                                <NavLink href="/" icon={<House className="size-4" />} label="Home" />
                                {tools.map((m) => (
                                    <ToolNav key={m.id} meta={m} />
                                ))}
                                {comingSoonTools.map((t) => (
                                    <ComingSoonNav key={t.id} tool={t} />
                                ))}
                            </nav>
                            {/* Settings: used now and then, so at the foot rather than among the tools. Inside ServiceM8, with a way out to more room. */}
                            {settings || inFrame ? (
                                <div className="space-y-0.5 border-t border-line px-2 py-2">
                                    {inFrame ? <NewTabLink /> : null}
                                    {settings ? <NavLink href="/settings" icon={<Settings className="size-4" />} label="Settings" /> : null}
                                </div>
                            ) : null}
                            <SidebarAccount me={me} company={company} realtime={realtime} canSettings={settings} />
                        </aside>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col">
                        <div className={phoneView ? undefined : "md:hidden"}>
                            {showTopBar && (
                                <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
                                    <Link href={withWindows("/")} className="font-bold text-ink no-underline">
                                        Outback Workbench
                                    </Link>
                                    <span className="hidden truncate text-sm text-muted sm:inline">{company.name}</span>
                                    <div className="ml-auto flex items-center gap-3">
                                        <SearchButton compact />
                                        <SyncStatus company={company} realtime={realtime} />
                                        <AccountMenu me={me} companyName={company.name} />
                                    </div>
                                </header>
                            )}
                            <nav className="tabs shrink-0" aria-label="Sections">
                                <Tab href="/" icon={<House className="size-4" />} label="Home" />
                                {tools.map((m) => (
                                    <Tab key={m.id} href={`/t/${m.id}`} icon={<Icon name={m.icon} className="size-4" />} label={m.name} />
                                ))}
                                {settings && <Tab href="/settings" icon={<Settings className="size-4" />} label="Settings" />}
                                {showTopBar ? null : (
                                    <div className="ml-auto flex items-center gap-2 pl-3">
                                        <SearchButton compact />
                                        <SyncStatus company={company} realtime={realtime} />
                                    </div>
                                )}
                            </nav>
                        </div>
                        <ConnectionBanner company={company} />
                        {/* The scrollbar's space always kept, so a page doesn't shift sideways as it gets long enough to scroll. Positioned
                        (relative): hidden labels and switches (sr-only, absolutely placed) inside it are placed in it, not against the
                        window, where those far down the page made the window itself scroll past the app. */}
                        <main className="relative min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">{props.children}</main>
                    </div>
                </div>
            </SearchHost>
        </CompanyContext>
    );
}

const useActive = (href: string) => {
    const [location] = useLocation();
    return href === "/" ? location === "/" : location.startsWith(href);
};

/** A sidebar link: green bar and bold when it's where you are. */
/**
 * Inside ServiceM8: this page in its own browser tab, for the whole screen instead of ServiceM8's
 * window, still signed in (lib/api.ts signedInUrl, worked out as it's clicked: the page may have changed).
 */
function NewTabLink() {
    return (
        <a
            href={signedInUrl()}
            target="_blank"
            rel="noreferrer"
            title="Opens this page in its own browser tab, with the whole screen to work in. You stay signed in."
            onClick={(e) => {
                e.currentTarget.href = signedInUrl();
            }}
            className="flex items-center gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2 text-sm text-ink no-underline transition hover:bg-surface-2"
        >
            <span className="text-muted">
                <ExternalLink className="size-4" />
            </span>
            Open in a new tab
        </a>
    );
}

/** `active`: it's where you are, when that isn't just the address being under its link (a tool's Catalogue: ToolNav). */
function NavLink({ href, icon, label, active: given }: Readonly<{ href: string; icon: ReactNode; label: string; active?: boolean }>) {
    const withWindows = useWithWindows();
    const under = useActive(href);
    const active = given ?? under;
    return (
        <Link
            href={withWindows(href)}
            aria-current={active ? "page" : undefined}
            className={clsx(
                "flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2 text-sm text-ink no-underline transition",
                active ? "border-brand-500 bg-surface-2 font-semibold" : "border-transparent hover:bg-surface-2",
            )}
        >
            <span className={active ? "text-brand-ink" : "text-muted"}>{icon}</span>
            {label}
        </Link>
    );
}

/** A tool on the way, in the sidebar: like a tool's link, but quiet and not a link, with "Coming soon" under its name (the sidebar's too narrow for both on one line). */
function ComingSoonNav({ tool }: Readonly<{ tool: ComingSoonTool }>) {
    return (
        <div className="flex cursor-default items-start gap-3 rounded-md border-l-[3px] border-transparent px-3 py-2 text-sm text-muted" title={tool.description} aria-disabled="true">
            <Icon name={tool.icon} className="mt-0.5 size-4 shrink-0" />
            <span className="min-w-0">
                <span className="block truncate">{tool.name}</span>
                <span className="block text-[11px] leading-tight">Coming soon</span>
            </span>
        </div>
    );
}

/**
 * A tool in the sidebar, with its sections (if any) always listed underneath, for one click to each. Sections from one with a
 * heading on (ToolSection.heading, e.g. the Price List's Catalogue) are an entry of their own, under that heading. A tool with
 * settings (ToolMeta.settings) has a gear at the end of its row, to its tab in Settings (and its heading's, at that heading's panel).
 */
function ToolNav({ meta }: Readonly<{ meta: ToolMeta }>) {
    const href = `/t/${meta.id}`;
    const [location] = useLocation();
    // What needs attention in each section (Home's: the same counts as its list), beside it.
    const alerts = useHome().data?.tools[meta.id]?.alerts ?? [];
    const alertOf = (section: string) => alerts.find((a) => a.section === section);
    const groups: { heading?: ToolSection["heading"]; sections: NonNullable<ToolMeta["sections"]> }[] = [];
    for (const s of meta.sections ?? []) {
        if (s.heading || !groups.length) groups.push({ heading: s.heading, sections: [] });
        groups.at(-1)?.sections.push(s);
    }
    const inGroup = (g: (typeof groups)[number]) => g.sections.some((s) => location.startsWith(`${href}/${s.id}`));
    // The tool's own entry is where you are anywhere in it, but a heading's sections.
    const inHeading = groups.slice(1).some(inGroup);
    if (!groups.length) return <NavLink href={href} icon={<Icon name={meta.icon} className="size-4" />} label={meta.name} />;
    return (
        <>
            {groups.map((g, i) => {
                const settingsAt = i === 0 ? "" : g.heading?.settingsAt;
                const gear = meta.settings && settingsAt !== undefined ? `/settings/${meta.id}${settingsAt ? "#" + settingsAt : ""}` : null;
                return (
                    <div key={g.sections[0]?.id ?? i} className="group/entry relative">
                        {g.heading ? (
                            <NavLink href={`${href}/${g.sections[0]?.id ?? ""}`} icon={<Icon name={g.heading.icon} className="size-4" />} label={g.heading.name} active={inGroup(g)} />
                        ) : (
                            <NavLink href={href} icon={<Icon name={meta.icon} className="size-4" />} label={meta.name} active={location.startsWith(href) && !inHeading} />
                        )}
                        {gear ? <SettingsGear href={gear} label={`${meta.name} settings`} /> : null}
                        <SubNav
                            href={href}
                            first={i === 0}
                            sections={g.sections.map((s) => ({ id: s.id, name: s.name, icon: <Icon name={s.icon} className="size-3.5" />, alert: alertOf(s.id) }))}
                        />
                    </div>
                );
            })}
        </>
    );
}

/** A tool's settings (its tab in Settings), as a gear at the end of its entry's row (over the entry's link, at the top of it): green while it's open. */
function SettingsGear({ href, label }: Readonly<{ href: string; label: string }>) {
    const withWindows = useWithWindows();
    const [location] = useLocation();
    const active = location.startsWith(href.split("#")[0] ?? href);
    return (
        <Link
            href={withWindows(href)}
            aria-label={label}
            title={label}
            aria-current={active ? "page" : undefined}
            className={clsx(
                "absolute top-1 right-1 grid size-7 place-items-center rounded-md no-underline transition hover:bg-canvas hover:text-ink",
                active ? "bg-canvas text-brand-ink" : "text-muted",
            )}
        >
            <Settings className="size-4" />
        </Link>
    );
}

/** Sections listed under a sidebar link, indented. The tool's `first` is what its plain address shows. */
function SubNav({ href, sections, first = true }: Readonly<{ href: string; sections: { id: string; name: string; icon: ReactNode; alert?: HomeAlert }[]; first?: boolean }>) {
    const withWindows = useWithWindows();
    const [location] = useLocation();
    return (
        <div className="mt-0.5 mb-1 ml-5 space-y-0.5 border-l border-line pl-2">
            {sections.map((s, i) => {
                const sectionHref = `${href}/${s.id}`;
                const active = location.startsWith(sectionHref) || (first && i === 0 && location.replace(/\/$/, "") === href);
                return (
                    <div key={s.id} className="relative">
                        <Link
                            href={withWindows(sectionHref)}
                            aria-current={active ? "page" : undefined}
                            className={clsx(
                                "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-ink no-underline transition [&_svg]:size-3.5",
                                active ? "bg-surface-2 font-semibold" : "hover:bg-surface-2",
                                s.alert && "pr-12",
                            )}
                        >
                            <span className={active ? "text-brand-ink" : "text-muted"}>{s.icon}</span>
                            <span className="min-w-0 truncate">{s.name}</span>
                        </Link>
                        {s.alert ? <AttentionCount alert={s.alert} /> : null}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * How many in a section need attention (its list's Needs attention), at the end of its row: red when any is (losing money, or
 * tripping up accounting software), else amber; none when nothing does. Its own link: to its list, showing just those.
 */
function AttentionCount({ alert }: Readonly<{ alert: HomeAlert }>) {
    const withWindows = useWithWindows();
    return (
        <Link
            href={withWindows(alert.href)}
            title={`${alert.count.toLocaleString()} ${alert.label}${alert.detail ? `: ${alert.detail}` : ""}`}
            aria-label={`${alert.count.toLocaleString()} ${alert.label}`}
            className={clsx(
                "absolute top-1/2 right-1.5 -translate-y-1/2 rounded-full px-1.5 text-[11px] leading-[18px] font-semibold tabular-nums no-underline",
                alert.tone === "red" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning-ink",
            )}
        >
            {alert.count > 99 ? "99+" : alert.count}
        </Link>
    );
}

function Tab({ href, icon, label }: Readonly<{ href: string; icon: ReactNode; label: string }>) {
    const withWindows = useWithWindows();
    const active = useActive(href);
    return (
        <Link href={withWindows(href)} className="tab" aria-current={active ? "page" : undefined}>
            {icon}
            {label}
        </Link>
    );
}

function useSignOut() {
    const qc = useQueryClient();
    return () => signOut(() => qc.clear());
}

/** Signed out, and to the sign-in page whatever happens (the server's session ends there when it can; failing that, it expires). */
export async function signOut(clear?: () => void) {
    try {
        await api("/auth/logout", { method: "POST" });
    } catch {
        // Unreachable (offline, restarting): gone from here all the same.
    } finally {
        clear?.();
        location.href = "/login";
    }
}

/**
 * The account menu: who you are and where, theme (not inside ServiceM8 on the web, where the app
 * stays light: Settings > Appearance explains), Settings (not on phones: appearance, and for
 * managers the ServiceM8 connection and sync, access, people), the privacy policy and terms (in a
 * new tab) and sign out. Settings lives here, not with the tools.
 */
function AccountDetails(props: Readonly<{ me: Me; companyName: string; canSettings: boolean; onDone: () => void; onSignOut: () => void }>) {
    const { me } = props;
    const withWindows = useWithWindows();
    return (
        <div className="space-y-2 p-2 text-ink">
            <div className="min-w-0 px-1">
                <div className="truncate text-sm font-semibold">{me.user.name ?? me.user.email}</div>
                {me.user.name ? <div className="truncate text-xs text-muted">{me.user.email}</div> : null}
                <div className="truncate text-xs text-muted">{props.companyName}</div>
            </div>
            {/* Not inside ServiceM8 on the web, where the app stays light (lib/theme.ts): Settings > Appearance explains, with a link out. */}
            {themeLocked ? null : (
                <div className="space-y-1 border-t border-line pt-2">
                    <div className="px-1 text-xs font-medium text-muted">Theme</div>
                    <ThemePicker compact />
                </div>
            )}
            <div className="space-y-0.5 border-t border-line pt-2">
                {props.canSettings ? (
                    <Link href={withWindows("/settings")} onClick={props.onDone} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-ink no-underline hover:bg-surface-2">
                        <Settings className="size-4 text-muted" /> Settings
                    </Link>
                ) : null}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={props.onDone} className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-ink no-underline hover:bg-surface-2">
                    <FileText className="size-4 text-muted" /> Privacy and terms
                </a>
                <button type="button" className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-danger hover:bg-surface-2" onClick={props.onSignOut}>
                    <LogOut className="size-4" /> Sign out
                </button>
            </div>
        </div>
    );
}

/** Narrow screens in a browser: your name in the top bar, opening the account menu. */
function AccountMenu({ me, companyName }: Readonly<{ me: Me; companyName: string }>) {
    const signOut = useSignOut();
    return (
        <Popover
            label="Account"
            width="w-64"
            buttonClassName="flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-sm text-ink hover:bg-surface-2"
            button={
                <>
                    {me.user.name ?? me.user.email} <ChevronDown className="size-4" />
                </>
            }
        >
            {(close) => <AccountDetails me={me} companyName={companyName} canSettings={false} onDone={close} onSignOut={() => void signOut()} />}
        </Popover>
    );
}

/**
 * The sidebar's foot, like ServiceM8's account corner: you, your business and the live sync
 * status; click for the account menu (opening upwards), where Settings and sign out are.
 */
function SidebarAccount(props: Readonly<{ me: Me; company: CompanySummary; realtime: ConnectionState; canSettings: boolean }>) {
    const { me, company } = props;
    const signOut = useSignOut();
    const [open, setOpen] = useState(false);
    const box = useRef<HTMLDivElement>(null);
    // Closes on a click elsewhere or Escape.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("pointerdown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("pointerdown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div ref={box} className="relative border-t border-line p-2">
            {open ? (
                <div className="card absolute inset-x-2 bottom-full mb-2">
                    <AccountDetails me={me} companyName={company.name} canSettings={props.canSettings} onDone={() => setOpen(false)} onSignOut={() => void signOut()} />
                </div>
            ) : null}
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                className={clsx("flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-left hover:bg-surface-2", open && "bg-surface-2")}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-ink">{me.user.name ?? me.user.email}</span>
                    <span className="block truncate text-xs text-muted">{company.name}</span>
                    <SyncLine company={company} realtime={props.realtime} />
                </span>
                <ChevronUp className={clsx("size-4 shrink-0 text-muted transition", !open && "rotate-180")} />
            </button>
        </div>
    );
}
